// Single source of truth for every record type the home feed knows about.
//
// Both the build-time prefetch script (Node) and the React app import this
// module. Adding a new collection should be a one-place change here, plus an
// entry in any renderer switch that wants a bespoke layout (otherwise the
// generic fallback kicks in).
//
// Each verb groups one or more NSIDs. NSIDs that span protocols carry a
// `source` tag (e.g. `bsky` | `grain` | `tangled` | `leaflet` | `standard` |
// `dame` | `teal`) so the UI can offer a secondary filter and renderers can
// branch on origin.
//
// Per-collection shape:
//   nsid:    AT Protocol lexicon NSID (the PDS collection name).
//   source:  Short tag identifying the network the record originates from.
//   kind:    'content' — the record is the thing being shown.
//            'reference' — the record points at another record (like, repost,
//                          favorite, follow, star, vote); the prefetch step
//                          resolves the subject into a `subject` field.
//            'appviewFeed' — fetched via the Bluesky AppView feed instead of
//                          listRecords; only used for `app.bsky.feed.post`.
//   subject: A tag describing what kind of subject we expect ('bsky.post' |
//            'bsky.profile' | 'bsky.list' | 'atproto' for "any at:// URI, look
//            up via PLC + getRecord"). Usual on reference records, whose whole
//            content IS the subject; a content record declares it too when it
//            backlinks something it has to be read against (a redaction is an
//            erasure OF a post, so it hydrates the post it blacked out).
//   subjectField: Which field on the record holds that backlink. Defaults to
//            `subject`; a lexicon that names it otherwise (redaction's
//            `source`) says so here.
//   max:     Per-collection cap for prefetch's listRecords pagination.
//   maxAgeDays: Optional cutoff. Records older than this are dropped before
//            being written into the unified feed (per-verb snapshots keep
//            them so per-page views aren't truncated).
//
// Per-verb shape:
//   verb:        The gerund the rest of the codebase uses to identify it.
//   collections: One or more
//                `{ nsid, source, kind, subject?, subjectField?, max, maxAgeDays? }`.
//   icon:        lucide-react icon name (resolved in VerbIcon.jsx).
//   renderer:    Component name used by FeedItem.jsx; null falls through to the
//                generic fallback. Renderers themselves live in src/components/.
//   recordHref:  Optional template for per-record page URL. `null` (the
//                default) means use the generic `/{nsid}/{rkey}` form.
//   pastTense:   Used by ReferenceCard's "Dame {pastTense} {subject}" label.
//   group:       Which head of the filter sheet's index the verb sits under:
//                words, making, noticing or social (VERB_GROUPS below).
//   gloss:       The filter sheet's one line on what the verb covers. Plain,
//                lower case, a few words; keep it true when collections change.
//   dedupe:      Optional. `'rkey'` collapses records that appear in more than
//                one of the verb's collections under the same rkey, keeping the
//                one from the collection listed first. For lexicons that moved
//                namespace without re-keying (teal.fm's alpha → production).

/**
 * Anisota records (`net.anisota.*`) have no bespoke record page on dame.is —
 * the shared Record.jsx hardcodes Bluesky fetching for the posting / reposting
 * verbs — so their feed rows link to the generic atproto explorer (which reads
 * any record straight off the PDS) instead of a `/posting/{rkey}` page that
 * would try to fetch a Bluesky post. Declared as a hoisted function so the
 * `recordHref` closures below can reference it.
 */
function anisotaRecordHref(atUri) {
  const m = String(atUri || '').match(/^at:\/\/([^/]+)\/([^/]+)\/([^/?#]+)/);
  return m ? `/exploring/${m[1]}/${m[2]}/${encodeURIComponent(m[3])}` : null;
}

export const VERB_REGISTRY = [
  {
    verb: 'logging',
    group: 'words',
    gloss: 'status lines, like “playing tennis”',
    icon: 'NotebookPen',
    renderer: 'StatusEntry',
    recordHref: ({ rkey }) => (rkey ? `/logging/${rkey}` : null),
    pastTense: 'logged',
    collections: [
      { nsid: 'is.dame.now', source: 'dame', kind: 'content', max: 500 },
    ],
  },
  {
    verb: 'posting',
    group: 'words',
    gloss: 'Bluesky and Anisota posts',
    icon: 'MessageCircle',
    renderer: 'PostCard',
    // Bluesky posts get a bespoke `/posting/{rkey}` page (Record.jsx);
    // Anisota posts (and their replies) have no such page, so they link out
    // to the generic explorer instead.
    recordHref: ({ rkey, atUri, source }) =>
      source === 'anisota'
        ? anisotaRecordHref(atUri)
        : rkey
          ? `/posting/${rkey}`
          : null,
    pastTense: 'posted',
    collections: [
      { nsid: 'app.bsky.feed.post', source: 'bsky', kind: 'appviewFeed', max: 200 },
      // Anisota posts + replies, authored on the user's own PDS. Read via
      // listRecords (there's no AppView for net.anisota.*); PostCard renders
      // them source-aware. Replies carry a `reply.parent.uri`, so they route
      // to the `replying` chip exactly like Bluesky replies.
      { nsid: 'net.anisota.feed.post', source: 'anisota', kind: 'content', max: 200 },
    ],
  },
  {
    verb: 'blogging',
    group: 'words',
    gloss: 'posts on standard.site',
    icon: 'BookOpen',
    renderer: 'BlogCard',
    pastTense: 'blogged',
    // Blog posts get the native /blogging/{rkey} reader (BlogPost.jsx matches
    // `:id` as an rkey; both standard.site and leaflet docs key by rkey).
    // Publications aren't posts, so they fall through to the generic
    // /{nsid}/{rkey} explorer. Mirrors BlogCard's own `blogHref` logic.
    recordHref: ({ atUri, rkey }) => {
      const nsid = nsidFromAtUri(atUri);
      return rkey && (nsid === 'site.standard.document' || nsid === 'pub.leaflet.document')
        ? `/blogging/${encodeURIComponent(rkey)}`
        : null;
    },
    collections: [
      { nsid: 'site.standard.document', source: 'standard', kind: 'content', max: 100 },
      { nsid: 'site.standard.publication', source: 'standard', kind: 'content', max: 50 },
    ],
  },
  {
    verb: 'listening',
    group: 'noticing',
    gloss: 'plays from teal.fm',
    icon: 'Headphones',
    renderer: 'ListenRow',
    recordHref: ({ rkey }) => (rkey ? `/listening/${rkey}` : null),
    pastTense: 'listened to',
    // teal.fm left its `alpha` namespace for production `fm.teal.*` in August
    // 2026. Both lexicons are ingested and collapsed by rkey — the migration
    // keeps rkeys, so a play written under both is one play — with production
    // winning because it's listed first. That ordering IS the priority, here
    // and in src/lib/teal.js, which reads these NSIDs back out.
    dedupe: 'rkey',
    collections: [
      { nsid: 'fm.teal.feed.play', source: 'teal', kind: 'content', max: 100 },
      { nsid: 'fm.teal.alpha.feed.play', source: 'teal', kind: 'content', max: 100 },
    ],
  },
  {
    verb: 'creating',
    group: 'making',
    gloss: 'works and projects',
    icon: 'Hammer',
    renderer: 'CreatingCard',
    pastTense: 'created',
    // Creative works are addressed by a human slug. Standard docs keep it in
    // `path`, legacy is.dame.creating.work in `slug`; fall back to the rkey.
    // (Slug logic is inlined to avoid a config ↔ registry import cycle.)
    recordHref: ({ payload, rkey }) => {
      const raw = payload?.slug || payload?.path || rkey || '';
      const slug = String(raw).replace(/^\/+/, '');
      return slug ? `/creating/${encodeURIComponent(slug)}` : null;
    },
    collections: [
      { nsid: 'is.dame.creating.work', source: 'dame', kind: 'content', max: 200 },
    ],
  },
  {
    verb: 'photographing',
    group: 'making',
    gloss: 'Grain galleries and stories',
    icon: 'Camera',
    renderer: 'MediaCard',
    pastTense: 'photographed',
    collections: [
      { nsid: 'social.grain.gallery', source: 'grain', kind: 'content', max: 100 },
      { nsid: 'social.grain.story', source: 'grain', kind: 'content', max: 100 },
    ],
  },
  {
    verb: 'mothing',
    group: 'noticing',
    gloss: 'moths, via iNaturalist',
    icon: 'Bug',
    renderer: 'ObservationCard',
    // Observation records are mirrored from iNaturalist onto the PDS by
    // scripts/mirror-inaturalist.mjs. Each moth gets a bespoke record page at
    // the short `/mothing/{rkey}` form (rkey = the iNaturalist observation id);
    // Record.jsx renders it with ObservationCard. The card's own thumbnail +
    // title still link out to the iNaturalist observation. Without this the feed
    // would fall through to the generic `/{nsid}/{rkey}` URL — but only the
    // `/mothing/{rkey}` form is wired for crawler cards (middleware.js matcher +
    // og/records.js RECORD_SECTIONS), so this keeps shared links carding.
    recordHref: ({ rkey }) => (rkey ? `/mothing/${rkey}` : null),
    pastTense: 'spotted',
    collections: [
      { nsid: 'is.dame.mothing.observation', source: 'inaturalist', kind: 'content', max: 500 },
    ],
  },
  {
    verb: 'observing',
    group: 'noticing',
    gloss: 'other iNaturalist sightings',
    icon: 'Binoculars',
    renderer: 'ObservationCard',
    // Every non-moth iNaturalist observation (birds, plants, fungi,
    // butterflies, other insects…). Mirrored alongside moths by
    // scripts/mirror-inaturalist.mjs, which splits each observation by
    // taxonomy; like mothing, each record links out to its iNat observation.
    pastTense: 'observed',
    // The cap has a second job here. Unlike moths — which the prefetch pulls
    // from iNaturalist in full for /mothing — these records ARE the whole
    // non-moth archive as the build sees it, and the first-sighting index is
    // derived from them (see writeFirstSightings in scripts/prefetch.mjs). Once
    // the collection outgrows this number the build can no longer tell a
    // species' first sighting from its oldest surviving one, so it stops
    // marking them and says so in the build log. Raise the cap then.
    collections: [
      { nsid: 'is.dame.observing.observation', source: 'inaturalist', kind: 'content', max: 500 },
    ],
  },
  {
    verb: 'liking',
    group: 'social',
    gloss: 'likes, favourites and stars',
    icon: 'Heart',
    renderer: 'ReferenceCard',
    pastTense: 'liked',
    collections: [
      { nsid: 'app.bsky.feed.like', source: 'bsky', kind: 'reference', subject: 'bsky.post', max: 200, maxAgeDays: 90 },
      { nsid: 'social.grain.favorite', source: 'grain', kind: 'reference', subject: 'atproto', max: 200, maxAgeDays: 90 },
      { nsid: 'sh.tangled.feed.star', source: 'tangled', kind: 'reference', subject: 'atproto', max: 200, maxAgeDays: 90 },
      { nsid: 'net.anisota.feed.like', source: 'anisota', kind: 'reference', subject: 'atproto', max: 200, maxAgeDays: 90 },
    ],
  },
  {
    verb: 'reposting',
    group: 'social',
    gloss: 'posts passed along',
    icon: 'Repeat2',
    // Repost records on the PDS are just pointers (`subject = { uri, cid }`).
    // The prefetch step hydrates each one's subject via
    // `app.bsky.feed.getPosts` and reshapes the row into the full PostCard
    // payload, so reposts can render inline like authored posts (with a
    // small "↻ reposted" badge and the original author header).
    renderer: 'PostCard',
    // Bluesky reposts render the hydrated original post (PostCard); Anisota
    // reposts are plain references (PostCard delegates them to ReferenceCard)
    // and, lacking a bespoke page, link out to the explorer.
    recordHref: ({ rkey, atUri, source }) =>
      source === 'anisota'
        ? anisotaRecordHref(atUri)
        : rkey
          ? `/reposting/${rkey}`
          : null,
    pastTense: 'reposted',
    collections: [
      { nsid: 'app.bsky.feed.repost', source: 'bsky', kind: 'reference', subject: 'bsky.post', max: 200, maxAgeDays: 90 },
      { nsid: 'net.anisota.feed.repost', source: 'anisota', kind: 'reference', subject: 'atproto', max: 200, maxAgeDays: 90 },
    ],
  },
  {
    verb: 'following',
    group: 'social',
    gloss: 'accounts followed',
    icon: 'UserPlus',
    renderer: 'ReferenceCard',
    pastTense: 'followed',
    collections: [
      { nsid: 'app.bsky.graph.follow', source: 'bsky', kind: 'reference', subject: 'bsky.profile', max: 200, maxAgeDays: 90 },
      { nsid: 'social.grain.graph.follow', source: 'grain', kind: 'reference', subject: 'bsky.profile', max: 200, maxAgeDays: 90 },
      { nsid: 'sh.tangled.graph.follow', source: 'tangled', kind: 'reference', subject: 'bsky.profile', max: 200, maxAgeDays: 90 },
    ],
  },
  {
    verb: 'listing',
    group: 'social',
    gloss: 'Bluesky lists',
    icon: 'ListChecks',
    renderer: 'ListCard',
    pastTense: 'curated',
    collections: [
      { nsid: 'app.bsky.graph.list', source: 'bsky', kind: 'content', max: 100, withMembers: 'app.bsky.graph.listitem' },
    ],
  },
  {
    verb: 'feeding',
    group: 'social',
    gloss: 'custom feeds',
    icon: 'Rss',
    renderer: 'GeneratorCard',
    pastTense: 'published a feed',
    collections: [
      { nsid: 'app.bsky.feed.generator', source: 'bsky', kind: 'content', max: 50 },
    ],
  },
  {
    verb: 'commenting',
    group: 'words',
    gloss: 'comments on Grain and Leaflet',
    icon: 'MessagesSquare',
    renderer: 'CommentCard',
    pastTense: 'commented',
    collections: [
      { nsid: 'social.grain.comment', source: 'grain', kind: 'content', max: 200, maxAgeDays: 180 },
      { nsid: 'pub.leaflet.comment', source: 'leaflet', kind: 'content', max: 200, maxAgeDays: 180 },
    ],
  },
  {
    // Flushes — `im.flushing.right.now` records written from flushes.app.
    // A flush is a sentence fragment that completes the author's handle
    // ("is flushing"), which is why FlushCard renders it the way a logging
    // status renders: the handle, then the fragment. Off by default in the
    // home feed (see DEFAULT_HOME_VERBS) — it's a high-frequency, low-stakes
    // status, so it's there for anyone who turns the chip on rather than
    // sitting in the timeline by default.
    verb: 'flushing',
    group: 'words',
    gloss: 'check-ins from Flushes',
    icon: 'Toilet',
    renderer: 'FlushCard',
    pastTense: 'flushed',
    recordHref: ({ rkey }) => (rkey ? `/flushing/${encodeURIComponent(rkey)}` : null),
    collections: [
      { nsid: 'im.flushing.right.now', source: 'flushes', kind: 'content', max: 200 },
    ],
  },
  {
    // Creative pieces made in the Anisota Lab's studios (Word Magnets,
    // Sigil, Carving, Inkblot, Redaction, Synth, Petri) plus authored spells.
    // One umbrella verb keeps the chip list tidy; AnisotaLabCard branches by
    // collection to render each piece (poem text, sigil figure, print, etc.).
    verb: 'crafting',
    group: 'making',
    gloss: 'Anisota lab pieces',
    icon: 'FlaskConical',
    renderer: 'AnisotaLabCard',
    pastTense: 'crafted',
    collections: [
      { nsid: 'net.anisota.lab.poetry', source: 'anisota', kind: 'content', max: 100 },
      { nsid: 'net.anisota.lab.sigil', source: 'anisota', kind: 'content', max: 100 },
      { nsid: 'net.anisota.spell.custom', source: 'anisota', kind: 'content', max: 100 },
      { nsid: 'net.anisota.lab.carving', source: 'anisota', kind: 'content', max: 100 },
      { nsid: 'net.anisota.lab.inkblot', source: 'anisota', kind: 'content', max: 100 },
      // An erasure poem is a reading of somebody else's post, so it carries a
      // `source` at-uri back to that post. Hydrating it gets the card the
      // author + timestamp it needs to show the piece as the post it erased.
      {
        nsid: 'net.anisota.lab.redaction',
        source: 'anisota',
        kind: 'content',
        subject: 'bsky.post',
        subjectField: 'source',
        max: 100,
      },
      { nsid: 'net.anisota.lab.synth', source: 'anisota', kind: 'content', max: 100 },
      { nsid: 'net.anisota.lab.petri', source: 'anisota', kind: 'content', max: 100 },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Derived lookups                                                     */
/* ------------------------------------------------------------------ */

const REGISTRY_BY_VERB = Object.fromEntries(VERB_REGISTRY.map((v) => [v.verb, v]));

const REGISTRY_BY_NSID = {};
for (const v of VERB_REGISTRY) {
  for (const c of v.collections) {
    REGISTRY_BY_NSID[c.nsid] = { verb: v, collection: c };
  }
}

/** All gerund verbs in registry order. Drives FeedFilters chip layout. */
export const VERBS = VERB_REGISTRY.map((v) => v.verb);

/**
 * The heads of the filter sheet's index, in the order they're read: what
 * Dame writes, what they make, what they notice, and what they do with other
 * people. Each verb names one in its `group`.
 */
export const VERB_GROUPS = ['words', 'making', 'noticing', 'social'];

/**
 * Verbs enabled in the home feed by default (when no `verbs=` URL param
 * is present). Everything left out is opt-in through the filter chips,
 * and stays genuinely uningested until then: Home derives its fetch set
 * from the active verbs, so an omitted verb costs no request on first
 * paint (see Home.jsx → fetchVerbs).
 *
 * Two kinds of verb are deliberately absent:
 *   - `liking`, whose reference records are high-volume and pay for a
 *     per-record subject lookup through the PLC;
 *   - `flushing`, which is frequent, small, and beside the point of the
 *     timeline — worth having, not worth defaulting to.
 *
 * Adding a verb here is what turns it on for everybody, so leaving one
 * out is a decision, not an oversight.
 */
export const DEFAULT_HOME_VERBS = [
  'logging',
  'posting',
  'blogging',
  'listening',
  'creating',
  'photographing',
  'mothing',
  'observing',
  'reposting',
  'following',
  'listing',
  'feeding',
  'commenting',
  'crafting',
];

/** Distinct source tags across the registry. Drives the source sub-filter. */
export const SOURCES = Array.from(
  new Set(VERB_REGISTRY.flatMap((v) => v.collections.map((c) => c.source))),
);

/** Look up a verb's full config. */
export function verbConfig(verb) {
  return REGISTRY_BY_VERB[verb] || null;
}

/** Look up which verb owns a given NSID (and the per-collection config). */
export function nsidConfig(nsid) {
  return REGISTRY_BY_NSID[nsid] || null;
}

/**
 * The verbs in `verbs` whose every collection is among `nsids` — given a build's
 * failed fetches (buildUnifiedFeed's `failures.labels`, where the author feed is
 * filed under `app.bsky.feed.post`), the verbs it got nothing at all for. An
 * unknown verb, or one with no collections, is never in the answer.
 */
export function verbsWithEverySourceIn(verbs, nsids) {
  const set = new Set(nsids || []);
  if (!set.size) return [];
  return Array.from(verbs || []).filter((verb) => {
    const collections = REGISTRY_BY_VERB[verb]?.collections || [];
    return collections.length > 0 && collections.every((c) => set.has(c.nsid));
  });
}

/** Verb -> primary NSID, used for routing/legacy lookups. */
export function primaryNsid(verb) {
  return REGISTRY_BY_VERB[verb]?.collections[0]?.nsid || null;
}

/** All NSIDs in the registry, deduped. */
export const NSIDS = Array.from(new Set(VERB_REGISTRY.flatMap((v) => v.collections.map((c) => c.nsid))));

/**
 * Given a verb + payload, return the canonical record-page path.
 *   - If the verb defines a custom `recordHref` template, use it.
 *   - Otherwise return the generic /{nsid}/{rkey} form, which Record.jsx
 *     handles via the registry-driven fallback.
 */
export function recordHrefFor(verb, { atUri, rkey, slug, source, payload } = {}) {
  const cfg = verbConfig(verb);
  if (!cfg) return null;
  if (typeof cfg.recordHref === 'function') {
    const href = cfg.recordHref({ atUri, rkey, slug, source, payload });
    if (href) return href;
  }
  // Generic fallback: /{nsid}/{rkey}
  const nsid = nsidFromAtUri(atUri) || cfg.collections.find((c) => c.source === source)?.nsid || cfg.collections[0]?.nsid;
  if (!nsid || !rkey) return null;
  return `/${nsid}/${encodeURIComponent(rkey)}`;
}

/** Collection NSID out of an at:// URI (e.g. "app.bsky.feed.post"). */
export function nsidFromAtUri(atUri) {
  if (!atUri) return null;
  const m = String(atUri).match(/^at:\/\/[^/]+\/([^/]+)\//);
  return m ? m[1] : null;
}

/**
 * Pretty label for a verb on the record page header (e.g. "a status",
 * "a post"). Defaults to a plain "a record" so unknown verbs still read.
 */
export const VERB_LABELS = {
  logging: 'a status',
  posting: 'a post',
  blogging: 'a blog post',
  listening: 'a play',
  creating: 'a work',
  photographing: 'a photo',
  mothing: 'a moth',
  observing: 'an observation',
  liking: 'a like',
  reposting: 'a repost',
  following: 'a follow',
  listing: 'a list',
  feeding: 'a feed',
  commenting: 'a comment',
  crafting: 'a lab piece',
  flushing: 'a flush',
};
